import CommitteeOpinionExtras from 'module/Form/CommitteeOpinionExtras';

import { Button, Form, Input } from 'antd';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useOfferListReturnURL } from 'hooks';
import { useSendCommitteeControlDecisionMutation } from 'services';
import { fetchError } from 'slices/commonSlice';
import Refund from 'components/ArticleOffer/offer/ArticleStatus/Refund';
import { committeeDecisions, confirmGroupSiblingsConflict, refundObjectParser } from 'utils';

const { TextArea } = Input;

/**
 * İH - Madde Kontrol karar formunun ortak gövdesi: karar ekranlarının hepsi bunu kullanır.
 *
 * @param {number} offerID Madde teklifi ID'si.
 * @param {number} status Gönderilecek heyet kararı kodu (`committeeDecisions`).
 * @param {number} step Maddenin süreç adımı; iade/ret kararlarında ücret iadesi bloğuna geçer.
 * @param {React.ReactNode} [children] Karara özel, formun en üstünde basılacak içerik (bilgi bandı vb.).
 * @param {boolean} [isSubmitDisabled=false] Gönder butonunu dışarıdan pasifleştirir. Kararın ön koşulu
 *   karar ekranında biliniyorsa kullanılır (ör. tercümeye gönderim için madde dosyasının yüklenmiş olması).
 */
const CommiteeDecisionForm = ({ offerID, status, step, children, isSubmitDisabled = false }) => {
  const [sendCommitteeControlDecision, { isLoading }] = useSendCommitteeControlDecisionMutation();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const offerListURL = useOfferListReturnURL();
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  /**
   * Kararı gönderir. Grup kardeş uyarısı gelirse kullanıcı onaylarsa aynı gövde `force: true` ile
   * yeniden gönderilir; BE gövdenin diğer alanlarının değişmemesini bekler.
   *
   * @param {object} body Karar isteğinin gövdesi.
   */
  const submitDecision = async (body) => {
    const { error } = await sendCommitteeControlDecision({ id: offerID, body });

    if (!body.force && confirmGroupSiblingsConflict(error, () => submitDecision({ ...body, force: true }))) return;

    if (error) return dispatch(fetchError(formatMessage({ id: 'article.form.fetchError' }, { error: error.status })));

    navigate(offerListURL);
  };

  const handleFinish = async (values) => {
    delete values.itemFile;
    delete values.idCard;
    let answers = {
      ...values,
    };
    refundObjectParser(answers);
    let newValues = {};
    if (status === committeeDecisions.rejected || status === committeeDecisions.revert) {
      newValues = {
        status,
        answers,
        mediaOfferDescription: values.description,
      };
    } else {
      newValues = {
        status,
        ...values,
      };
    }

    await submitDecision(newValues);
  };

  return (
    <Form
      layout="vertical"
      onFinish={handleFinish}
      form={form}
    >
      {children}

      <CommitteeOpinionExtras offerID={offerID} />
      {[committeeDecisions.rejected, committeeDecisions.revert].includes(status) ? (
        <Form.Item>
          <Refund
            status={status}
            committee="committee_control"
            step={step}
          />
        </Form.Item>
      ) : (
        <Form.Item
          name="description"
          className="gx-mb-4"
          rules={[
            {
              min: 50,
              message: formatMessage({
                id: 'article.reasonLengthMessageShort',
              }),
            },
          ]}
          normalize={(value) => (value.trim() === '' ? undefined : value)}
        >
          <TextArea
            rows={8}
            placeholder="Notunuz varsa girebilirsiniz"
            showCount
          />
        </Form.Item>
      )}
      <Button
        block
        htmlType="submit"
        disabled={isSubmitDisabled}
        loading={isLoading}
      >
        Gönder
      </Button>
    </Form>
  );
};

export default CommiteeDecisionForm;
